import type { CompanySupplierType } from '@crm/entities/account';
import type { CompanyType } from '@crm/entities/contact';
import type { CompanySortField } from '@travel-planner/shared/schemas/company';
import { companySupplierType } from '@crm/entities/account';
import { companyType } from '@crm/entities/contact';
import { companySortFields } from '@travel-planner/shared/schemas/company';

const companyTypeValues = Object.values(companyType) as readonly string[];
const companySupplierTypeValues = Object.values(
  companySupplierType
) as readonly string[];
const isSupplierOnly = (types?: readonly string[]) =>
  types?.length === 1 && types[0] === companyType.SUPPLIER;

const sortOrderValues = ['asc', 'desc'] as const;
type SortOrder = (typeof sortOrderValues)[number];

export type AccountsSearch = {
  name?: string;
  email?: string;
  companyType?: CompanyType[];
  supplierType?: CompanySupplierType[];
  organizationIds?: string[];
  label?: string[];
  page?: number;
  pageSize?: number;
  sortBy?: CompanySortField;
  sortOrder?: SortOrder;
};

const asStringArray = <T extends string>(
  v: unknown,
  allowed: readonly string[]
): T[] | undefined =>
  Array.isArray(v)
    ? v.filter((x): x is T => typeof x === 'string' && allowed.includes(x))
    : undefined;

const asIdArray = (v: unknown): string[] | undefined =>
  Array.isArray(v)
    ? v.filter((x): x is string => typeof x === 'string')
    : undefined;

const asSortField = (v: unknown): CompanySortField | undefined =>
  typeof v === 'string' && (companySortFields as readonly string[]).includes(v)
    ? (v as CompanySortField)
    : undefined;

const asSortOrder = (v: unknown): SortOrder | undefined =>
  typeof v === 'string' && (sortOrderValues as readonly string[]).includes(v)
    ? (v as SortOrder)
    : undefined;

export const validateAccountsSearch = (
  input: Record<string, unknown>
): AccountsSearch => {
  const selectedCompanyTypes = asStringArray<CompanyType>(
    input.companyType,
    companyTypeValues
  );

  return {
    name: typeof input.name === 'string' ? input.name : undefined,
    email: typeof input.email === 'string' ? input.email : undefined,
    companyType: selectedCompanyTypes,
    supplierType: isSupplierOnly(selectedCompanyTypes)
      ? asStringArray<CompanySupplierType>(
          input.supplierType,
          companySupplierTypeValues
        )
      : undefined,
    organizationIds: asIdArray(input.organizationIds),
    label: asIdArray(input.label)?.filter((label) => label.length > 0),
    page: typeof input.page === 'number' ? input.page : undefined,
    pageSize: typeof input.pageSize === 'number' ? input.pageSize : undefined,
    sortBy: asSortField(input.sortBy),
    sortOrder: asSortOrder(input.sortOrder),
  };
};
